export interface CalcItem {
  id: string;
  label: string;
  icon: string;
  volume: number; // cubic yards
  weight: number; // kg approx
}

export interface CalcRoom {
  id: string;
  label: string;
  icon: string;
  accentColor: string;
  bgColor: string;
  items: CalcItem[];
}

export interface LoadSize {
  id: string;
  label: string;
  minYards: number;
  maxYards: number;
  price: number;
  description: string;
  icon: string;
  popular?: boolean;
}

export const loadSizes: LoadSize[] = [
  { id: "mini", label: "Mini Load", minYards: 0, maxYards: 1, price: 109, description: "8 bin bags or a washing machine · 1 wheelie bin", icon: "ri-inbox-line" },
  { id: "small", label: "Small Load", minYards: 1, maxYards: 2, price: 155, description: "16 bin bags or 2 washing machines · 2 wheelie bins", icon: "ri-archive-2-line" },
  { id: "medium", label: "Medium Load", minYards: 2, maxYards: 4, price: 219, description: "30 bin bags or corner sofa · 4 wheelie bins", icon: "ri-box-3-line", popular: true },
  { id: "large", label: "Large Load", minYards: 4, maxYards: 7, price: 299, description: "45 bin bags or corner sofa + 2 armchairs · 7 wheelie bins", icon: "ri-truck-line" },
  { id: "xlarge", label: "X-Large Load", minYards: 7, maxYards: 10, price: 399, description: "60 bin bags or half of a Luton Van · 10 wheelie bins", icon: "ri-truck-line" },
  { id: "xxlarge", label: "XX-Large Load", minYards: 10, maxYards: 14, price: 459, description: "90 bin bags — a full load", icon: "ri-truck-line" },
];

export const homeRooms: CalcRoom[] = [
  {
    id: "kitchen",
    label: "Kitchen",
    icon: "ri-fridge-line",
    accentColor: "#f59e0b",
    bgColor: "#FEF3C7",
    items: [
      { id: "k-washing-machine", label: "Washing Machine", icon: "ri-recycle-line", volume: 0.75, weight: 70 },
      { id: "k-tumble-dryer", label: "Tumble Dryer", icon: "ri-recycle-line", volume: 0.75, weight: 40 },
      { id: "k-dishwasher", label: "Dishwasher", icon: "ri-drop-line", volume: 0.5, weight: 45 },
      { id: "k-fridge-freezer", label: "Fridge Freezer", icon: "ri-temp-cold-line", volume: 1.0, weight: 90 },
      { id: "k-oven", label: "Oven / Cooker", icon: "ri-fire-line", volume: 0.75, weight: 55 },
      { id: "k-microwave", label: "Microwave", icon: "ri-timer-line", volume: 0.25, weight: 15 },
      { id: "k-small-appliance", label: "Small Appliance", icon: "ri-plug-line", volume: 0.15, weight: 10 },
    ],
  },
  {
    id: "living-room",
    label: "Living Room",
    icon: "ri-sofa-line",
    accentColor: "#6CC412",
    bgColor: "#6CC412",
    items: [
      { id: "lr-sofa-2", label: "2 Seater Sofa", icon: "ri-sofa-line", volume: 2.0, weight: 60 },
      { id: "lr-sofa-3", label: "3 Seater Sofa", icon: "ri-sofa-line", volume: 2.5, weight: 80 },
      { id: "lr-corner-sofa", label: "Corner Sofa", icon: "ri-sofa-line", volume: 3.5, weight: 120 },
      { id: "lr-armchair", label: "Armchair", icon: "ri-armchair-line", volume: 1.0, weight: 35 },
      { id: "lr-coffee-table", label: "Coffee Table", icon: "ri-table-line", volume: 0.5, weight: 20 },
      { id: "lr-tv", label: "TV / Monitor", icon: "ri-tv-line", volume: 0.25, weight: 20 },
      { id: "lr-bookshelf", label: "Bookshelf", icon: "ri-archive-line", volume: 0.75, weight: 30 },
      { id: "lr-cabinet", label: "Cabinet / Sideboard", icon: "ri-archive-2-line", volume: 1.0, weight: 45 },
    ],
  },
  {
    id: "bedroom",
    label: "Bedroom",
    icon: "ri-hotel-bed-line",
    accentColor: "#8b5cf6",
    bgColor: "#EDE9FE",
    items: [
      { id: "bd-bed-single", label: "Single Bed Frame", icon: "ri-hotel-bed-line", volume: 1.5, weight: 30 },
      { id: "bd-bed-double", label: "Double / Queen Bed Frame", icon: "ri-hotel-bed-line", volume: 2.0, weight: 40 },
      { id: "bd-mattress-single", label: "Single Mattress", icon: "ri-hotel-bed-fill", volume: 1.0, weight: 20 },
      { id: "bd-mattress-double", label: "Double / Queen Mattress", icon: "ri-hotel-bed-fill", volume: 1.5, weight: 30 },
      { id: "bd-mattress-king", label: "King Mattress", icon: "ri-hotel-bed-fill", volume: 2.0, weight: 40 },
      { id: "bd-wardrobe-2", label: "2 Door Wardrobe", icon: "ri-door-open-line", volume: 2.0, weight: 60 },
      { id: "bd-wardrobe-3", label: "3 Door Wardrobe", icon: "ri-door-open-line", volume: 2.5, weight: 80 },
      { id: "bd-chest-drawers", label: "Chest of Drawers", icon: "ri-inbox-2-line", volume: 0.75, weight: 30 },
      { id: "bd-bedside-table", label: "Bedside Table", icon: "ri-table-line", volume: 0.25, weight: 15 },
    ],
  },
  {
    id: "bathroom",
    label: "Bathroom",
    icon: "ri-water-flash-line",
    accentColor: "#0ea5e9",
    bgColor: "#E0F2FE",
    items: [
      { id: "ba-bathtub", label: "Bathtub", icon: "ri-water-flash-line", volume: 2.0, weight: 100 },
      { id: "ba-toilet", label: "Toilet", icon: "ri-water-flash-line", volume: 0.5, weight: 25 },
      { id: "ba-sink", label: "Bathroom Sink", icon: "ri-water-flash-line", volume: 0.5, weight: 20 },
      { id: "ba-shower", label: "Shower Tray / Enclosure", icon: "ri-drop-line", volume: 0.75, weight: 30 },
      { id: "ba-cabinet", label: "Bathroom Cabinet", icon: "ri-archive-2-line", volume: 0.25, weight: 15 },
    ],
  },
  {
    id: "garden",
    label: "Garden",
    icon: "ri-plant-line",
    accentColor: "#6CC412",
    bgColor: "#6CC412",
    items: [
      { id: "ga-dining-4", label: "4 Seat Garden Dining Set", icon: "ri-plant-line", volume: 2.0, weight: 60 },
      { id: "ga-bench", label: "Garden Bench", icon: "ri-plant-line", volume: 0.75, weight: 30 },
      { id: "ga-lawnmower", label: "Lawnmower", icon: "ri-scissors-line", volume: 0.5, weight: 35 },
      { id: "ga-trampoline", label: "Trampoline", icon: "ri-community-line", volume: 3.0, weight: 80 },
      { id: "ga-bags", label: "Shed Rubbish Bags", icon: "ri-delete-bin-line", volume: 0.12, weight: 10 },
    ],
  },
  {
    id: "garage-loft",
    label: "Garage / Loft",
    icon: "ri-home-gear-line",
    accentColor: "#ef4444",
    bgColor: "#FEE2E2",
    items: [
      { id: "gl-bag-heavy", label: "Heavy Duty Bag (90L)", icon: "ri-delete-bin-line", volume: 0.12, weight: 15 },
      { id: "gl-bag-standard", label: "Standard Bag (60L)", icon: "ri-delete-bin-2-line", volume: 0.08, weight: 10 },
      { id: "gl-bike", label: "Bicycle", icon: "ri-bike-line", volume: 0.5, weight: 15 },
      { id: "gl-pushchair", label: "Pushchair / Pram", icon: "ri-riding-line", volume: 0.5, weight: 12 },
      { id: "gl-boxes", label: "Cardboard Boxes / Misc", icon: "ri-box-1-line", volume: 0.12, weight: 8 },
    ],
  },
];

export const officeRooms: CalcRoom[] = [
  {
    id: "office-space",
    label: "Office Space",
    icon: "ri-computer-line",
    accentColor: "#6CC412",
    bgColor: "#6CC412",
    items: [
      { id: "os-office-desk", label: "Office Desk", icon: "ri-computer-line", volume: 1.5, weight: 40 },
      { id: "os-office-chair", label: "Office Chair", icon: "ri-layout-bottom-2-line", volume: 0.5, weight: 15 },
      { id: "os-filing-cabinet", label: "Filing Cabinet", icon: "ri-inbox-2-line", volume: 0.75, weight: 40 },
      { id: "os-monitor", label: "Desktop PC / Monitor", icon: "ri-tv-line", volume: 0.25, weight: 10 },
      { id: "os-printer", label: "Printer / Copier", icon: "ri-printer-line", volume: 0.5, weight: 25 },
      { id: "os-pedestal", label: "Pedestal / Under-desk Drawers", icon: "ri-inbox-line", volume: 0.5, weight: 20 },
      { id: "os-shelving", label: "Shelving Unit", icon: "ri-archive-line", volume: 1.0, weight: 30 },
    ],
  },
  {
    id: "meeting-room",
    label: "Meeting Room",
    icon: "ri-team-line",
    accentColor: "#f59e0b",
    bgColor: "#FEF3C7",
    items: [
      { id: "mr-conf-table", label: "Conference Table", icon: "ri-table-line", volume: 3.0, weight: 100 },
      { id: "mr-conf-chair", label: "Conference Chair", icon: "ri-layout-bottom-2-line", volume: 0.5, weight: 12 },
      { id: "mr-tv-display", label: "TV / Display Screen", icon: "ri-tv-2-line", volume: 0.5, weight: 20 },
      { id: "mr-whiteboard", label: "Whiteboard / Pinboard", icon: "ri-artboard-line", volume: 0.5, weight: 15 },
      { id: "mr-projector", label: "Projector / AV Equipment", icon: "ri-vidicon-line", volume: 0.25, weight: 10 },
    ],
  },
  {
    id: "reception",
    label: "Reception / Lobby",
    icon: "ri-building-2-line",
    accentColor: "#8b5cf6",
    bgColor: "#EDE9FE",
    items: [
      { id: "rc-reception-desk", label: "Reception Desk", icon: "ri-computer-line", volume: 2.0, weight: 80 },
      { id: "rc-waiting-chair", label: "Waiting / Guest Chair", icon: "ri-armchair-line", volume: 0.5, weight: 12 },
      { id: "rc-display-stand", label: "Display Stand / Rack", icon: "ri-stack-line", volume: 0.5, weight: 15 },
      { id: "rc-sofa", label: "Reception Sofa", icon: "ri-sofa-line", volume: 2.0, weight: 60 },
      { id: "rc-coffee-table", label: "Coffee Table", icon: "ri-table-line", volume: 0.5, weight: 20 },
    ],
  },
  {
    id: "kitchen-breakout",
    label: "Kitchen / Break Room",
    icon: "ri-fridge-line",
    accentColor: "#6CC412",
    bgColor: "#6CC412",
    items: [
      { id: "kb-fridge", label: "Fridge / Fridge Freezer", icon: "ri-temp-cold-line", volume: 1.0, weight: 90 },
      { id: "kb-microwave", label: "Microwave", icon: "ri-timer-line", volume: 0.25, weight: 15 },
      { id: "kb-dishwasher", label: "Dishwasher", icon: "ri-drop-line", volume: 0.5, weight: 45 },
      { id: "kb-vending-machine", label: "Vending Machine", icon: "ri-store-2-line", volume: 1.5, weight: 200 },
      { id: "kb-table-set", label: "Kitchen Table &amp; Chairs (set)", icon: "ri-layout-bottom-2-line", volume: 1.5, weight: 40 },
    ],
  },
  {
    id: "storage-it",
    label: "Storage / IT Room",
    icon: "ri-server-line",
    accentColor: "#ef4444",
    bgColor: "#FEE2E2",
    items: [
      { id: "si-server-rack", label: "Server Rack", icon: "ri-server-line", volume: 1.0, weight: 100 },
      { id: "si-shelving-heavy", label: "Heavy Duty Shelving Unit", icon: "ri-archive-line", volume: 1.5, weight: 60 },
      { id: "si-archive-box", label: "Archive Boxes", icon: "ri-box-1-line", volume: 0.12, weight: 12 },
      { id: "si-lockers", label: "Lockers (per unit)", icon: "ri-lock-line", volume: 0.75, weight: 40 },
      { id: "si-safe", label: "Safe / Security Box", icon: "ri-safe-line", volume: 0.5, weight: 80 },
    ],
  },
];

export type CalcType = "home" | "office";
export type Selections = Record<string, number>;
